import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Star, MessageSquare, BadgeCheck, Eye, EyeOff, Building2, ChevronDown } from "lucide-react";
import { FindoSpinner } from "@/components/FindoSpinner";

export default function Reviews() {
  const { t } = useLang();
  const [reviews, setReviews] = useState(null);
  const [estFilter, setEstFilter] = useState("all");

  async function load() {
    try { setReviews(await api("/partner/reviews")); }
    catch (e) { toast.error(e.message); setReviews([]); }
  }
  useEffect(() => { load(); }, []);

  const estOptions = React.useMemo(() => {
    const m = new Map();
    (reviews || []).forEach((r) => { if (r.establishment_id && r.establishment_name) m.set(r.establishment_id, r.establishment_name); });
    return Array.from(m, ([id, name]) => ({ id, name }));
  }, [reviews]);
  const shown = (reviews || []).filter((r) => estFilter === "all" || r.establishment_id === estFilter);

  return (
    <div className="p-6 md:p-10 max-w-3xl mx-auto">
      <PageHeader title={t("db.reviews.t")} subtitle={t("db.reviews.s")} testid="reviews-header" />

      {estOptions.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-6" data-testid="reviews-est-filter">
          {[{ id: "all", name: t("prev.allSites") }, ...estOptions].map((o) => (
            <button key={o.id} onClick={() => setEstFilter(o.id)} data-testid={`reviews-est-chip-${o.id}`}
              className={`inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-sm font-semibold transition-colors ${estFilter === o.id ? "bg-findo-navy text-white" : "bg-white border border-border text-slate-600 hover:bg-slate-50"}`}>
              {o.id !== "all" && <Building2 size={14} />} {o.name}
            </button>
          ))}
        </div>
      )}

      {reviews === null ? (
        <div className="space-y-4" data-testid="reviews-skeleton">
          {Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-40 rounded-2xl bg-slate-100 animate-pulse" />)}
        </div>
      ) : shown.length === 0 ? (
        <div className="text-center py-24 border-2 border-dashed border-border rounded-2xl">
          <MessageSquare size={32} className="mx-auto text-slate-300" />
          <p className="mt-3 text-slate-500">{t("prev.none")}</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="partner-reviews-list">
          {shown.map((r) => <ReviewItem key={r.id} review={r} onSaved={load} t={t} />)}
        </div>
      )}
    </div>
  );
}

function ReviewItem({ review, onSaved, t }) {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [text, setText] = useState(review.partner_reply?.body || "");
  const [isPublic, setIsPublic] = useState(review.partner_reply?.public !== false);
  const [busy, setBusy] = useState(false);
  const hasReply = !!review.partner_reply;
  const replyPublic = review.partner_reply?.public !== false;
  const dateStr = review.created_at
    ? new Date(review.created_at).toLocaleDateString(document.documentElement.lang || "fr-FR", { day: "2-digit", month: "short", year: "numeric" })
    : "";

  async function send() {
    if (text.trim().length < 3) return;
    setBusy(true);
    try {
      await api(`/partner/reviews/${review.id}/reply`, { method: "POST", body: { body: text.trim(), public: isPublic } });
      toast.success(t("prev.published"));
      setOpen(false);
      onSaved();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }

  return (
    <div className="bg-white rounded-2xl border border-border card-shadow overflow-hidden" data-testid="partner-review-card">
      {/* Compact row: client name, rating and date. Click to expand the detail. */}
      <button type="button" onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-3 p-4 text-left" data-testid={`review-compact-${review.id}`}>
        <span className="flex items-center gap-0.5 shrink-0">
          {[1, 2, 3, 4, 5].map((i) => (
            <Star key={i} size={14} className={i <= review.rating ? "fill-findo-red text-findo-red" : "text-slate-200"} />
          ))}
        </span>
        <span className="font-semibold text-slate-700 text-sm truncate" data-testid={`review-compact-author-${review.id}`}>{review.author_name}</span>
        {review.verified && <BadgeCheck size={14} className="text-emerald-600 shrink-0" />}
        <span className="ml-auto flex items-center gap-3 text-xs text-slate-400 shrink-0">
          <span data-testid={`review-compact-date-${review.id}`}>{dateStr}</span>
          <ChevronDown size={16} className={`transition-transform ${expanded ? "rotate-180" : ""}`} />
        </span>
      </button>

      {expanded && (
        <div className="px-6 pb-6 pt-1 border-t border-border" data-testid={`review-detail-${review.id}`}>
          {review.establishment_name && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-findo-navy/5 text-findo-navy px-2.5 py-1 text-[11px] font-bold mt-3" data-testid="review-establishment-badge">
              <Building2 size={12} /> {review.establishment_name}
            </span>
          )}
          {review.title && <p className="font-display text-lg font-semibold text-findo-navy mt-3">{review.title}</p>}
          <p className="mt-1 text-slate-600 text-[15px]" data-testid={`review-body-${review.id}`}>{review.body}</p>

          {hasReply && !open && (
            <div className="mt-4 rounded-xl bg-slate-50 border-l-4 border-findo-navy p-3" data-testid="partner-reply-block">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-bold text-findo-navy uppercase tracking-wide">{t("prev.yourReply")}</p>
                <span
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold ${replyPublic ? "bg-emerald-50 text-emerald-700" : "bg-slate-200 text-slate-600"}`}
                  data-testid="partner-reply-visibility-badge">
                  {replyPublic ? <><Eye size={12} /> {tf(t, "prev.replyPublic", "Publique")}</> : <><EyeOff size={12} /> {tf(t, "prev.replyPrivate", "Masquée")}</>}
                </span>
              </div>
              <p className="mt-1 text-sm text-slate-600">{review.partner_reply.body}</p>
            </div>
          )}

          {open ? (
            <div className="mt-4 space-y-2" data-testid="partner-reply-editor">
              <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder={t("prev.replyPh")} className="rounded-xl min-h-24" data-testid="partner-reply-input" />
              <label className="flex items-center gap-2.5 text-sm text-slate-600 cursor-pointer select-none" data-testid="partner-reply-public-label">
                <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-findo-navy focus:ring-findo-navy/30" data-testid="partner-reply-public-checkbox" />
                <span className="inline-flex items-center gap-1.5">
                  {isPublic ? <Eye size={14} className="text-emerald-600" /> : <EyeOff size={14} className="text-slate-400" />}
                  {tf(t, "prev.makePublic", "Afficher cette réponse sur ma fiche publique")}
                </span>
              </label>
              <div className="flex gap-2">
                <Button onClick={send} disabled={busy} className="rounded-xl font-semibold bg-findo-navy hover:bg-findo-navy/90" data-testid="partner-reply-send">
                  {busy ? <span className="inline-flex items-center gap-1.5"><FindoSpinner size={16} />{t("prev.sending")}</span> : t("prev.send")}
                </Button>
                <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-xl">{t("c.cancel")}</Button>
              </div>
            </div>
          ) : (
            <Button variant="outline" size="sm" onClick={() => setOpen(true)} className="mt-4 rounded-full font-semibold border-findo-navy text-findo-navy" data-testid="partner-reply-toggle">
              <MessageSquare size={15} className="mr-1.5" /> {hasReply ? t("prev.editReply") : t("prev.reply")}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// Small i18n fallback helper: returns `fallback` when the key is missing.
function tf(t, key, fallback) {
  const v = t ? t(key) : key;
  return v === key ? fallback : v;
}
